/**
 * Fallback limit of units per product code, per passenger.
 * Mirrors the `PRODUCTS_MAX_QUANTITY_PER_ITEM` default in `config/defaultFeatures.js`
 * and guards against a whitelabel configuring a non-positive value.
 *
 * @type {number}
 */
export const DEFAULT_MAX_QUANTITY_PER_ITEM = 3;

/**
 * Calculate the maximum quantity a passenger can select of a single product in a segment
 * Combines the two independent constraints: the stock left after the other passengers'
 * selections, and the per-item limit configured for the whitelabel
 *
 * @param {Object} params - Parameters object
 * @param {number} params.stock - Total stock of the product in the segment
 * @param {number} params.totalSelected - Quantity selected across all passengers, including this one
 * @param {number} params.currentQuantity - Quantity already selected by this passenger
 * @param {number|string} [params.maxPerItem] - Per-item limit. Coerced with `Number` because the
 *   whitelabel config is merged raw from JSON, so a numeric string is a valid shape. Truncated to
 *   an integer first, then falls back to DEFAULT_MAX_QUANTITY_PER_ITEM unless the result is a
 *   positive integer. Booleans always fall back. Note that `0` is not expressible as
 *   "no products": it falls back to the default.
 * @returns {number} Maximum selectable quantity, never negative
 *
 * @example
 * calculateMaxSelectableQuantity({ stock: 500, totalSelected: 0, currentQuantity: 0, maxPerItem: 3 });
 * // Returns: 3 — the limit is more restrictive than the stock
 *
 * calculateMaxSelectableQuantity({ stock: 2, totalSelected: 0, currentQuantity: 0, maxPerItem: 3 });
 * // Returns: 2 — the stock is more restrictive than the limit
 *
 * calculateMaxSelectableQuantity({ stock: 500, totalSelected: 0, currentQuantity: 0, maxPerItem: '5' });
 * // Returns: 5 — a numeric string from the whitelabel is honored, not silently downgraded
 */
export function calculateMaxSelectableQuantity({
  stock,
  totalSelected,
  currentQuantity,
  maxPerItem,
}) {
  // Truncate BEFORE validating, so a fractional value below 1 falls back to the default
  // instead of resolving to 0 and disabling the increase control for the whole catalog.
  // Booleans are rejected outright: `Number(true)` is 1, and most whitelabel features are
  // booleans, so a mistyped `true` would silently cap every product at a single unit.
  const configuredLimit = typeof maxPerItem === 'boolean' ? NaN : Math.floor(Number(maxPerItem));
  const limit =
    Number.isFinite(configuredLimit) && configuredLimit > 0
      ? configuredLimit
      : DEFAULT_MAX_QUANTITY_PER_ITEM;

  // Stock left for this passenger excludes what the other passengers already hold
  const availableStock = (stock || 0) - ((totalSelected || 0) - (currentQuantity || 0));

  return Math.max(0, Math.min(availableStock, limit));
}

/**
 * Calculate total quantity selected for a product code across all passengers in a segment
 * Used to determine available stock considering all passengers' selections
 *
 * @param {Map} selectedProducts - Immutable Map of selected products
 * @param {string} segment - Segment: 'outgoing' or 'incoming'
 * @param {string} productCode - Product code to calculate total for
 * @returns {number} Total quantity selected
 */
export function calculateTotalQuantityByProductCode(selectedProducts, segment, productCode) {
  if (!selectedProducts || selectedProducts.size === 0) {
    return 0;
  }

  let totalQuantity = 0;
  selectedProducts.forEach((passengerProducts) => {
    const segmentProducts = passengerProducts.get(segment);
    if (segmentProducts && segmentProducts.size > 0) {
      const product = segmentProducts.find((p) => p.get('code') === productCode);
      if (product) {
        totalQuantity += product.get('quantity') || 0;
      }
    }
  });

  return totalQuantity;
}

/**
 * Calculate total quantities selected for all product codes across all passengers in a segment
 * Returns an object keyed by product code with total quantity selected
 * More efficient than calling calculateTotalQuantityByProductCode multiple times
 *
 * @param {Map} selectedProducts - Immutable Map of selected products
 * @param {string} segment - Segment: 'outgoing' or 'incoming'
 * @returns {Object} Object keyed by product code with total quantity selected
 *
 * @example
 * const totals = calculateAllTotalQuantitiesByProductCode(selectedProducts, 'outgoing');
 * // Returns: { 'BEB001': 5, 'SNK002': 3 }
 */
export function calculateAllTotalQuantitiesByProductCode(selectedProducts, segment) {
  const totalQuantitiesByCode = {};

  if (!selectedProducts || selectedProducts.size === 0) {
    return totalQuantitiesByCode;
  }

  selectedProducts.forEach((passengerProducts) => {
    const segmentProducts = passengerProducts.get(segment);
    if (segmentProducts && segmentProducts.size > 0) {
      segmentProducts.forEach((product) => {
        const code = product.get('code');
        const quantity = product.get('quantity') || 0;
        totalQuantitiesByCode[code] = (totalQuantitiesByCode[code] || 0) + quantity;
      });
    }
  });

  return totalQuantitiesByCode;
}
